
import React, { useState, useEffect, useRef } from 'react';
import { Moon, Sun, Orbit, Check, Sparkles, X, Loader2, ArrowRight, ShieldCheck, Search, Calendar, Clock, Mail, Phone, User, CreditCard, MapPin } from 'lucide-react';
import { MembershipTier, LocationSuggestion, MemberRecord } from '../types';
import { joinMembership, suggestLocations } from '../services/geminiService';
import PayPalButtons from './PayPalButtons';

const TIERS: MembershipTier[] = [
  {
    id: 'lunar',
    name: 'Lunar Seeker',
    price: '0',
    description: 'Align your rhythm with the moon. Free forever.',
    accentColor: 'border-slate-400 text-slate-400 bg-slate-400/5',
    icon: 'Moon',
    features: [
      'Completely Free Forever',
      'Detailed Birth Chart Analysis',
      'Daily Personalized Horoscopes',
      'Messaging with Allie Directly',
      'Compatibility Charts'
    ]
  },
  {
    id: 'solar',
    name: 'Solar Initiate',
    price: '33',
    description: 'Command your personal power through deeper celestial study.',
    accentColor: 'border-amber-500 text-amber-500 bg-amber-500/5',
    icon: 'Sun',
    popular: true,
    features: [
      'Everything in Lunar Seeker',
      '(1) 15-min Video/Phone/Message Session',
      '1 Exclusive Content Piece per Month',
      '5% Discount on All Readings'
    ]
  },
  {
    id: 'galactic',
    name: 'Galactic Master',
    price: '88',
    description: 'Total integration with the cosmic order and direct mentorship.',
    accentColor: 'border-indigo-500 text-indigo-500 bg-indigo-500/5',
    icon: 'Orbit',
    features: [
      'Everything in Solar Initiate',
      '(1) 30-min Video/Phone/Message Session',
      '2 Exclusive Content Pieces per Month',
      '10% Discount on All Readings'
    ]
  }
];

const IconMap: Record<string, any> = { Moon, Sun, Orbit };

interface MembershipProps {
  onJoinSuccess?: (member: MemberRecord) => void;
}

const Membership: React.FC<MembershipProps> = ({ onJoinSuccess }) => {
  const [selectedTier, setSelectedTier] = useState<MembershipTier | null>(null);
  const [formData, setFormData] = useState({ 
    name: '', email: '', phone: '', birthDate: '', birthTime: '', birthLocation: '',
    lat: undefined as number | undefined, lng: undefined as number | undefined
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState<LocationSuggestion[]>([]);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  
  // Ref to prevent suggestions from popping up after selection
  const preventNextSuggestion = useRef(false);

  useEffect(() => {
    if (!selectedTier || preventNextSuggestion.current) {
      preventNextSuggestion.current = false;
      return;
    }
    
    const timer = setTimeout(async () => {
      if (formData.birthLocation && formData.birthLocation.length > 3 && !isSearchingLocation) {
        setIsSearchingLocation(true);
        const results = await suggestLocations(formData.birthLocation);
        setLocationSuggestions(results);
        setShowLocationSuggestions(results.length > 0);
        setIsSearchingLocation(false);
      } else if (!formData.birthLocation || formData.birthLocation.length <= 3) {
        setLocationSuggestions([]);
        setShowLocationSuggestions(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [formData.birthLocation, selectedTier]);

  const handleInitiate = async (e: React.FormEvent) => {
    e.preventDefault();
    const required = ['name', 'email', 'phone', 'birthDate', 'birthTime', 'birthLocation'];
    const missing = required.filter(key => !formData[key as keyof typeof formData]?.toString().trim());
    if (missing.length !== 0) {
      alert(`Missing required information: ${missing.join(', ')}`);
      return;
    }

    if (formData.lat === undefined || formData.lng === undefined) {
      alert("Please select a precise origin from the suggestions to ensure mathematical degree accuracy.");
      return;
    }

    if (selectedTier?.price === '0') handleFinalizeRegistration({ id: 'FREE-' + Date.now() });
    else setShowPayment(true);
  };

  const handleFinalizeRegistration = async (details: any) => {
    if (!selectedTier) return;
    setIsSubmitting(true);
    try {
      const result = await joinMembership({
        name: formData.name, email: formData.email, phone: formData.phone,
        birth_date: formData.birthDate, birth_time: formData.birthTime,
        birth_location: formData.birthLocation, tier: selectedTier.name,
        payment_id: details.id,
        lat: formData.lat,
        lng: formData.lng
      });
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setSelectedTier(null);
        if (result.member && onJoinSuccess) onJoinSuccess(result.member);
      }, 800);
    } catch (error) { alert("Registry failed."); }
    finally { setIsSubmitting(false); }
  };

  return (
    <section id="membership" className="py-24 bg-slate-950/20 relative overflow-hidden backdrop-blur-[2px]">
      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-amber-400 text-sm tracking-[0.2em] uppercase mb-2">Soul Circles</h2>
          <h3 id="membership-title" className="text-4xl md:text-5xl font-serif text-slate-50">Celestial Memberships</h3>
          <div className="w-24 h-1 bg-amber-500 mx-auto mt-6 shadow-[0_0_10px_rgba(245,158,11,0.5)]" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {TIERS.map((tier) => {
            const Icon = IconMap[tier.icon];
            return (
              <div key={tier.id} className={`relative group p-8 bg-slate-900/40 backdrop-blur-xl border-2 rounded-sm transition-all duration-700 hover:-translate-y-4 ${tier.accentColor} ${tier.popular ? 'shadow-[0_0_40px_rgba(245,158,11,0.15)] ring-1 ring-amber-500/30' : 'border-indigo-900/30'}`}>
                {tier.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-amber-500 text-slate-950 text-[10px] uppercase font-bold px-4 py-1.5 rounded-full shadow-lg">Most Popular</div>}
                <div className="flex flex-col items-center text-center mb-10">
                  <div className={`p-4 rounded-full mb-6 ${tier.popular ? 'bg-amber-500 text-slate-950' : 'bg-slate-950/80 border border-indigo-900/50'}`}><Icon size={32} /></div>
                  <h4 className="text-2xl font-serif text-slate-50 mb-2">{tier.name}</h4>
                  <div className="flex items-baseline gap-1 mb-4"><span className="text-sm text-indigo-400 font-mono">$</span><span className="text-4xl font-serif font-bold text-slate-50">{tier.price}</span></div>
                </div>
                <ul className="space-y-4 mb-10">{tier.features.map((feature, i) => (<li key={i} className="flex gap-3 text-sm text-indigo-100/70"><Check size={16} className={`${tier.popular ? 'text-amber-500' : 'text-indigo-400'} flex-shrink-0 mt-0.5`} /><span>{feature}</span></li>))}</ul>
                <button onClick={() => { setSelectedTier(tier); setShowPayment(false); }} className={`w-full py-4 uppercase tracking-[0.2em] text-[10px] font-bold transition-all rounded-sm flex items-center justify-center gap-2 ${tier.popular ? 'bg-amber-500 text-slate-950 hover:bg-amber-400' : 'bg-indigo-600/10 border border-indigo-500/50 text-indigo-100 hover:bg-indigo-600/20'}`}>Join the Circle</button>
              </div>
            );
          })}
        </div>

        {selectedTier && (
          <div className="fixed inset-0 z-[200000] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md animate-fade-in overflow-y-auto">
            <div className="w-full max-w-2xl bg-slate-900 border border-amber-500/30 p-6 md:p-10 rounded-sm shadow-2xl relative my-8">
               <div className="absolute top-0 right-0 p-4"><button onClick={() => { setSelectedTier(null); setShowPayment(false); }} className="text-indigo-500 hover:text-white"><X size={24} /></button></div>
               {success ? (
                 <div className="text-center py-10 animate-fade-in">
                   <div className="w-16 h-16 bg-amber-500 text-slate-950 rounded-full flex items-center justify-center mx-auto mb-6"><Check size={32} /></div>
                   <h4 className="text-2xl font-serif text-white mb-2 uppercase tracking-widest">Registry Synchronized</h4>
                   <p className="text-indigo-300 text-sm italic">"One moment as we open the sanctuary doors..."</p>
                 </div>
               ) : showPayment ? (
                 <div className="animate-fade-in text-center">
                    <h4 className="text-2xl font-serif text-white uppercase tracking-widest mb-8">Vault Authorization</h4>
                    <PayPalButtons amount={parseFloat(selectedTier.price)} description={`Membership: ${selectedTier.name}`} onSuccess={handleFinalizeRegistration} onError={() => alert("Treasury interrupted.")} />
                    <button onClick={() => setShowPayment(false)} className="mt-6 text-indigo-500 text-[9px] uppercase font-bold tracking-widest">Back</button>
                 </div>
               ) : (
                 <div className="max-h-[85vh] overflow-y-auto pr-2 custom-scrollbar">
                   <div className="text-center mb-10"><h4 className="text-2xl font-serif text-white uppercase tracking-widest">Sacred Registry</h4></div>
                   <form onSubmit={handleInitiate} className="space-y-6">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                       <div className="col-span-2"><label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold mb-2 block">Seeker Name *</label><input required type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-50 focus:border-amber-500 outline-none text-sm rounded-sm" /></div>
                       <div><label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold mb-2 block">Soul Email *</label><input required type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-50 focus:border-amber-500 outline-none text-sm rounded-sm" /></div>
                       <div><label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold mb-2 block">Connection Line *</label><input required type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-50 focus:border-amber-500 outline-none text-sm rounded-sm" /></div>
                       <div><label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold mb-2 block">Birth Date *</label><input required type="text" placeholder="YYYY-MM-DD" value={formData.birthDate} onChange={e => setFormData({...formData, birthDate: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-50 focus:border-amber-500 outline-none text-sm rounded-sm" /></div>
                       <div><label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold mb-2 block">Birth Time *</label><input required type="text" placeholder="HH:MM (24h)" value={formData.birthTime} onChange={e => setFormData({...formData, birthTime: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-50 focus:border-amber-500 outline-none text-sm rounded-sm" /></div>
                       <div className="col-span-2 relative">
                         <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold mb-2 block">Birth Origin * {isSearchingLocation && <Loader2 size={10} className="animate-spin text-amber-500 inline ml-2" />}</label>
                         <input required type="text" value={formData.birthLocation} autoComplete="off" onFocus={() => locationSuggestions.length > 0 && !preventNextSuggestion.current && setShowLocationSuggestions(true)} onChange={e => setFormData({...formData, birthLocation: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-50 focus:border-amber-500 outline-none text-sm rounded-sm" placeholder="City, Country" />
                         {showLocationSuggestions && (
                          <div className="absolute z-50 w-full mt-2 bg-slate-950 border border-amber-500/50 shadow-2xl rounded-sm overflow-hidden animate-fade-in">
                            <ul className="max-h-48 overflow-y-auto">
                              {locationSuggestions.map((s, idx) => (
                                <li key={idx}><button type="button" onClick={() => { 
                                  preventNextSuggestion.current = true;
                                  setFormData({...formData, birthLocation: s.name, lat: s.lat, lng: s.lng}); 
                                  setShowLocationSuggestions(false); 
                                }} className="w-full text-left px-4 py-3 text-[11px] uppercase tracking-wider text-indigo-100 hover:bg-amber-500 hover:text-slate-950 transition-colors flex justify-between items-center">
                                  <span>{s.name}</span>
                                  <span className="text-[8px] opacity-40 font-mono">{s.lat.toFixed(2)} / {s.lng.toFixed(2)}</span>
                                </button></li>
                              ))}
                            </ul>
                          </div>
                        )}
                       </div>
                     </div>
                     <button type="submit" className="w-full py-5 bg-amber-500 text-slate-950 font-bold uppercase tracking-widest text-[10px] hover:bg-amber-400 transition-all shadow-xl rounded-sm">Initiate Ritual</button>
                   </form>
                 </div>
               )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Membership;
